import React from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import { INDUSTRIES } from '../../data/industriesData';
import { SOLUTIONS } from '../../data/solutionsData';

const SITE_URL = 'https://versalaunch.com';
const DEFAULT_OG_IMAGE = `${SITE_URL}/images/SocialShare.png`;
const DEFAULT_META = {
  title: 'Versa Launch',
  description: 'Versa Launch builds and manages the technology behind modern businesses. Websites, communications, AI, software and growth infrastructure, all under one roof.',
  type: 'website'
};

const routeMeta = {
  '/': {
    title: 'Versa Launch - Business Technology Company',
    description: 'Versa Launch builds and manages the technology businesses rely on every day, from websites and phone systems to AI reception, software, search visibility, automation and ongoing support.',
    type: 'website'
  },
  '/plans': {
    title: 'Plans - Versa Launch',
    description: 'Compare Versa Launch plans for local businesses and choose the setup that matches your growth goals and team size.',
    type: 'website'
  },
  '/features': {
    title: 'Features - Versa Launch',
    description: 'Explore the full Versa Launch feature set, including website tools, local SEO, AI phone workflows, and lead automation.',
    type: 'website'
  },
  '/portfolio': {
    title: 'Projects - Versa Launch',
    description: 'Explore a premium portfolio gallery of live client websites, with site previews and automatic live-status filtering.',
    type: 'website'
  },
  '/solutions': {
    title: 'Solutions - Versa Launch',
    description: 'Six pillars, one company. Website and commerce, communications, AI reception, search and growth, business software, and ongoing IT support, built and managed by Versa Launch.',
    type: 'website'
  },
  '/services/custom-design': {
    title: 'Custom Design Services - Versa Launch',
    description: 'Get a conversion-focused custom website design tailored to your business, brand, and local market.',
    type: 'website'
  },
  '/services/ai-phone-assistant': {
    title: 'AI Phone Assistant - Versa Launch',
    description: 'Answer every customer call with an AI phone assistant that handles FAQs, captures leads, and supports booking workflows.',
    type: 'website'
  },
  '/services/business-phone': {
    title: 'Business Phone Line - Versa Launch',
    description: 'Set up a professional business phone line with smart call routing, voicemail, and call handling built for local teams.',
    type: 'website'
  },
  '/services/client-portals': {
    title: 'Client Portals and Forms - Versa Launch',
    description: 'Launch branded client portals and intake forms that simplify scheduling, communication, and service delivery.',
    type: 'website'
  },
  '/services/web-apps': {
    title: 'Web Apps - Versa Launch',
    description: 'Build custom web apps that automate operations, improve customer experience, and support business growth.',
    type: 'website'
  },
  '/services/rewards': {
    title: 'Rewards Program Tools - Versa Launch',
    description: 'Increase repeat business with rewards and loyalty workflows designed for local service businesses.',
    type: 'website'
  },
  '/services/square-integrations': {
    title: 'Square Integrations - Versa Launch',
    description: 'Connect Versa Launch with Square to sync customer workflows, payments, and business operations.',
    type: 'website'
  },
  '/book': {
    title: 'Book a Call - Versa Launch',
    description: 'Schedule a free visibility review call with Versa Launch. We\'ll walk through your online presence, answer questions, and map out next steps.',
    type: 'website'
  },
  '/vendors': {
    title: 'Vendor Registration - Versa Launch Events',
    description: 'Register as a vendor for Versa Launch events in Jacksonville: markets, pop-ups, and day-into-night parties. Two minutes, and you get first look when a booth opens.',
    type: 'website'
  },
  '/resources': {
    title: 'Resources - Versa Launch',
    description: 'Read practical growth resources for local businesses, covering launch checklists, SEO, marketing, and retention.',
    type: 'website'
  },
  '/resources/getting-started': {
    title: 'Getting Started Guide - Versa Launch',
    description: 'Follow the Versa Launch getting started guide to launch your online presence and start attracting local customers.',
    type: 'article'
  },
  '/resources/marketing-strategies': {
    title: 'Marketing Strategies for Local Businesses - Versa Launch',
    description: 'Use these marketing strategies to improve lead quality, increase customer acquisition, and grow service revenue.',
    type: 'article'
  },
  '/resources/seo-guide': {
    title: 'Local SEO Guide - Versa Launch',
    description: 'Learn the local SEO fundamentals that help service businesses rank higher in search and convert more traffic.',
    type: 'article'
  },
  '/resources/case-studies': {
    title: 'Case Studies - Versa Launch',
    description: 'See real case studies showing how local businesses improved visibility, lead flow, and customer retention.',
    type: 'article'
  },
  '/resources/growth-tips': {
    title: 'Growth Tips - Versa Launch',
    description: 'Apply fast, high-impact growth tips to improve customer response rates, increase bookings, and drive repeat jobs.',
    type: 'article'
  },
  '/resources/best-practices': {
    title: 'Best Practices Playbook - Versa Launch',
    description: 'Use proven website, lead handling, and follow-up best practices to create a stronger customer experience.',
    type: 'article'
  },
  '/privacy-policy': {
    title: 'Privacy Policy - Versa Launch',
    description: 'Review the Versa Launch privacy policy for details on data collection, usage, and protection practices.',
    type: 'website'
  },
  '/terms-of-service': {
    title: 'Terms of Service - Versa Launch',
    description: 'Read the Versa Launch terms of service, including platform usage terms, limitations, and responsibilities.',
    type: 'website'
  },
  '/competition-report': {
    title: 'Competition Report - Versa Launch',
    description: 'Get a competition report to see how your business performs online and where to improve first.',
    type: 'website',
    robots: 'noindex, nofollow'
  },
  '/hidden/version': {
    title: 'Version - Versa Launch',
    description: 'Internal build version information.',
    type: 'website',
    robots: 'noindex, nofollow'
  },
  '/hidden/checkout/9B600ce30cGGdF63N2gQE03': {
    title: 'Checkout Redirect - Versa Launch',
    description: 'Secure checkout redirect endpoint.',
    type: 'website',
    robots: 'noindex, nofollow'
  },
  '/ai-context': {
    title: 'AI Context - Versa Launch',
    description: 'AI-readable context and platform information for Versa Launch.',
    type: 'website',
    robots: 'all'
  },
  '/ai-index': {
    title: 'AI Context - Versa Launch',
    description: 'AI-readable context and platform information for Versa Launch.',
    type: 'website',
    robots: 'all'
  },
  '/api/page-context': {
    title: 'AI Context - Versa Launch',
    description: 'AI-readable context and platform information for Versa Launch.',
    type: 'website',
    robots: 'all'
  },
  '/industries': {
    title: 'Industries We Serve - Versa Launch',
    description:
      'Versa Launch builds growth platforms for restaurants, contractors, online stores, salons, gyms, and more local businesses. One platform that fits how you work.',
    type: 'website'
  }
};

// Solution pillar pages share their SEO copy with the page content.
SOLUTIONS.forEach((sol) => {
  routeMeta[`/solutions/${sol.slug}`] = {
    title: sol.seoTitle,
    description: sol.seoDescription,
    type: 'website'
  };
});

// Per-industry landing pages share their SEO copy with the page content.
INDUSTRIES.forEach((ind) => {
  routeMeta[`/industries/${ind.slug}`] = {
    title: ind.seoTitle,
    description: ind.seoDescription,
    type: 'website'
  };
});

function RouteMeta() {
  const { pathname } = useLocation();
  const meta = routeMeta[pathname] || DEFAULT_META;
  const canonicalPath = pathname === '/' ? '/' : pathname.replace(/\/+$/, '').replace(/\/index\.html$/, '');
  const canonicalUrl = `${SITE_URL}${canonicalPath}`;
  const pageType = meta.type || DEFAULT_META.type;

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': pageType === 'article' ? 'Article' : 'WebPage',
    name: meta.title,
    description: meta.description,
    url: canonicalUrl,
    inLanguage: 'en-US',
    isPartOf: {
      '@type': 'WebSite',
      name: 'Versa Launch',
      url: SITE_URL
    }
  };

  const websiteSchema = canonicalPath === '/' ? {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Versa Launch',
    url: SITE_URL,
    inLanguage: 'en-US'
  } : null;

  return (
    <Helmet>
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <meta name="robots" content={meta.robots || 'index, follow'} />
      <meta name="author" content="Versa Launch" />
      <link rel="canonical" href={canonicalUrl} />

      <meta property="og:site_name" content="Versa Launch" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:type" content={pageType} />
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={DEFAULT_OG_IMAGE} />
      <meta property="og:image:secure_url" content={DEFAULT_OG_IMAGE} />
      <meta property="og:image:alt" content="Versa Launch preview image" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.description} />
      <meta name="twitter:image" content={DEFAULT_OG_IMAGE} />

      <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>
      {websiteSchema && (
        <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      )}
    </Helmet>
  );
}

export default RouteMeta;
